import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, FileText, Target, Activity, Clock, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { API_URL as CONFIG_API_URL } from '../config';

const API_URL = CONFIG_API_URL || 'http://8.141.122.155:3002/api';

export default function OmniSearch({ projectId }) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState({ tasks: [], keywords: [], brands: [] });
  const [loading, setLoading] = useState(false);
  const wrapperRef = useRef(null);
  const navigate = useNavigate();

  // Handle Cmd+K / Ctrl+K
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsOpen(true);
      }
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Close on outside click
  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [wrapperRef]);

  // Fetch search results
  useEffect(() => {
    if (!query) {
      setResults({ tasks: [], keywords: [], brands: [] });
      return;
    }
    
    const fetchResults = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API_URL}/search/global?q=${encodeURIComponent(query)}&projectId=${projectId || 'all'}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setResults(res.data || { tasks: [], keywords: [], brands: [] });
      } catch (err) {
        console.error('Search failed', err);
      } finally {
        setLoading(false);
      }
    };

    const delayDebounceFn = setTimeout(() => {
      fetchResults();
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [query, projectId]);

  const handleSelectKeyword = (keyword) => {
    setIsOpen(false);
    navigate(`/mention-records?keyword=${encodeURIComponent(keyword)}`);
  };

  const handleSelectTask = (taskId) => {
    setIsOpen(false);
    navigate(`/tasks/${taskId}/results`);
  };

  const handleSelectBrand = (brandId) => {
    setIsOpen(false);
    // Ideally this would switch the global project context, but for now we'll just go to brand insights
    navigate(`/brand-insights`);
  };

  return (
    <div className="relative flex-1" ref={wrapperRef}>
      {/* Input trigger */}
      <div 
        className="relative flex items-center cursor-pointer"
        onClick={() => setIsOpen(true)}
      >
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-500 w-5 h-5" />
        <input
          type="text"
          className="w-full pl-10 pr-12 py-2 border border-gray-200 dark:border-slate-700 rounded-full bg-gray-50 dark:bg-slate-800 focus:bg-white dark:focus:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 transition-colors placeholder-gray-400 dark:placeholder-slate-500 text-sm dark:text-slate-200"
          placeholder="搜索报告、词条... (Cmd+K)"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setIsOpen(true);
          }}
          onClick={() => setIsOpen(true)}
        />
        <div className="absolute right-3 top-1/2 -translate-y-1/2 flex gap-1 pointer-events-none opacity-50">
           <span className="text-xs bg-gray-200 dark:bg-slate-700 px-1.5 py-0.5 rounded text-gray-600 dark:text-slate-300">⌘</span>
           <span className="text-xs bg-gray-200 dark:bg-slate-700 px-1.5 py-0.5 rounded text-gray-600 dark:text-slate-300">K</span>
        </div>
      </div>

      {/* Dropdown Panel */}
      {isOpen && (
        <div className="absolute top-full mt-2 w-full min-w-[400px] max-w-xl bg-white dark:bg-slate-800 rounded-xl shadow-2xl border border-gray-100 dark:border-slate-700 overflow-hidden z-50 animate-in fade-in slide-in-from-top-2 duration-200">
          <div className="max-h-[70vh] overflow-y-auto p-2">
            
            {!query && (
              <div className="p-4 text-center">
                <Activity className="w-8 h-8 text-indigo-400 mx-auto mb-2 opacity-50" />
                <p className="text-sm text-gray-500 dark:text-slate-400">输入关键词开始全局检索...</p>
                <div className="mt-4 flex flex-col gap-2 text-left">
                  <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider px-2">💡 快捷建议</div>
                  <div 
                    onClick={() => handleSelectKeyword('Model 3')}
                    className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700/50 rounded-lg cursor-pointer"
                  >
                    <Target className="w-4 h-4 text-blue-500" />
                    查看近期的核心词条明细
                  </div>
                </div>
              </div>
            )}

            {loading && query && (
              <div className="p-4 text-center text-sm text-gray-500 dark:text-slate-400 flex items-center justify-center gap-2">
                 <div className="w-4 h-4 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
                 检索中...
              </div>
            )}

            {!loading && query && (results.keywords?.length || 0) === 0 && (results.tasks?.length || 0) === 0 && (results.brands?.length || 0) === 0 && (
               <div className="p-4 text-center text-sm text-gray-500 dark:text-slate-400">
                  未找到与 "{query}" 相关的结果
               </div>
            )}

            {!loading && query && (
              <>
                {/* Keywords Section */}
                {(results.keywords?.length || 0) > 0 && (
                  <div className="mb-4">
                    <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider px-3 mb-2 flex items-center gap-1">
                      <Target className="w-3 h-3" /> 监控词条
                    </div>
                    {(results.keywords || []).map((kw, i) => (
                      <div 
                        key={i}
                        onClick={() => handleSelectKeyword(kw.keyword)}
                        className="flex items-center justify-between px-3 py-2 text-sm text-gray-700 dark:text-slate-200 hover:bg-blue-50 dark:hover:bg-slate-700 rounded-lg cursor-pointer group transition-colors"
                      >
                        <span className="font-medium group-hover:text-blue-600 dark:group-hover:text-blue-400">{kw.keyword}</span>
                        <div className="flex items-center gap-2">
                           {kw.positiveRate > 0 && (
                             <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
                               正面 {kw.positiveRate}%
                             </span>
                           )}
                           <span className="text-[10px] text-gray-400 group-hover:text-blue-500">查看明细 &rarr;</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Tasks Section */}
                {(results.tasks?.length || 0) > 0 && (
                  <div className="mb-4">
                    <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider px-3 mb-2 flex items-center gap-1">
                      <FileText className="w-3 h-3" /> 分析报告
                    </div>
                    {(results.tasks || []).map((task, i) => (
                      <div 
                        key={i}
                        onClick={() => handleSelectTask(task.id)}
                        className="flex items-center justify-between px-3 py-2 text-sm text-gray-700 dark:text-slate-200 hover:bg-indigo-50 dark:hover:bg-slate-700 rounded-lg cursor-pointer group transition-colors"
                      >
                        <div className="flex flex-col">
                           <span className="font-medium group-hover:text-indigo-600 dark:group-hover:text-indigo-400">{task.name}</span>
                           <span className="text-[10px] text-gray-400 flex items-center gap-1">
                              <Clock className="w-3 h-3" /> 
                              {new Date(task.createdAt).toLocaleDateString()}
                           </span>
                        </div>
                        <span className="text-[10px] px-2 py-1 bg-gray-100 dark:bg-slate-600 rounded text-gray-500 dark:text-slate-300">
                           {task.status === 'completed' ? '已完成' : '分析中'}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Brands Section */}
                {(results.brands?.length || 0) > 0 && (
                  <div>
                    <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider px-3 mb-2 flex items-center gap-1">
                      <Activity className="w-3 h-3" /> 品牌 / 项目
                    </div>
                    {(results.brands || []).map((brand, i) => (
                      <div 
                        key={i}
                        onClick={() => handleSelectBrand(brand.id)}
                        className="flex items-center justify-between px-3 py-2 text-sm text-gray-700 dark:text-slate-200 hover:bg-purple-50 dark:hover:bg-slate-700 rounded-lg cursor-pointer group transition-colors"
                      >
                        <span className="font-medium group-hover:text-purple-600 dark:group-hover:text-purple-400">{brand.name}</span>
                        <span className="text-[10px] text-gray-400 group-hover:text-purple-500">进入沙盘 &rarr;</span>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
